import type { Permission, RoleCode } from '@thrivehrm/shared';
import { createContext, useContext } from 'react';

/**
 * The signed-in user as the UI sees it. The API remains the source of truth for every
 * permission check (ARCHITECTURE §6); the UI only hides what the user cannot use.
 */
export interface CurrentUser {
  userId: string;
  employeeId: string | null;
  name: string;
  email: string;
  roles: readonly RoleCode[];
  permissions: ReadonlySet<Permission>;
  hasReports: boolean;
}

export interface AuthState {
  user: CurrentUser | null;
}

export const AuthContext = createContext<AuthState>({ user: null });

export function useAuth(): AuthState {
  return useContext(AuthContext);
}

/** True when the user holds any of `permissions` (empty list = everyone signed in). */
export function hasAnyPermission(
  user: CurrentUser | null,
  permissions: readonly Permission[] = [],
): boolean {
  if (!user) return false;
  return permissions.length === 0 || permissions.some((p) => user.permissions.has(p));
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts.at(-1)?.[0] ?? '') : '';
  return `${first}${last}`.toUpperCase();
}
